import { useLocation } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import {
  buildRecentSearchDisplayPayload,
  convertYearKeyToRange,
  decodeLocationV2Entry,
  expandIndustryParentToChildren,
  getFilterValuesWithExtraValues,
  LEADS_PER_PAGE,
  suggestionToLocationItem,
  V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY,
  isPositiveCompanyId,
} from '../../helpers/helper';
import {
  INDUSTRY_DATA,
  LOCATION_DATA,
  SENIORITY_GROUP_OPTIONS,
  DEPARTMENT_GROUP_OPTIONS,
} from '../../helpers/filter-data';
import {
  getCompaniesV2,
  getCompaniesV2Count,
  getSavedCompanies,
  getSavedCompaniesV2Count,
  getCompanyListsAPI,
  getCompanyNamesAPI,
  getSignalsAPI,
  getNewsAPI,
} from '../../helpers/leads.api';
import {
  GetCompaniesV2CountResponse,
  LeadFinderSecondaryTab,
  LeadFinderPrimaryTab,
  V2Company,
  LeadFinderListTab,
  LeadFinderListVisibility,
  VisitorByGenderFilter,
} from '../../types';
import toaster from '../../../../shared/toaster';
import {
  getUserTimeZone,
  leadFinderBulkOperationLimit,
} from '../../../../shared/utils/user-details';
import { StorageUtil } from '../../../../shared/utils/storage';
import { RootState } from '../../../../store/root-reducer';

// Cached whole-database company total shown in the default (no-filter) view so we
// can skip the expensive { tab: 'total' } count query. Update periodically.
const DEFAULT_TOTAL_COMPANIES_COUNT = 41680721;

// netNew for the cached default view = cachedTotal - savedCount.
// Unknown saved -> undefined (New badge shows skeleton). Negative -> cachedTotal.
const deriveDefaultNetNew = (
  saved: number | undefined,
  total: number,
): number | undefined => {
  if (saved == null) return undefined;
  const net = total - saved;
  return net < 0 ? total : net;
};

export const useCompanyFinder = (
  activeSubTab: LeadFinderSecondaryTab,
  shouldBlockAccess = false,
  activeLeadType?: LeadFinderPrimaryTab,
  skipUrlPushForBulkSearchRef?: React.MutableRefObject<boolean>,
) => {
  const location = useLocation();
  const userEmail = useSelector((state: RootState) => state?.home?.email);
  const isEmailVerified = useSelector(
    (state: RootState) => state?.home?.verified,
  );

  const isSavedSubTab = activeSubTab === LeadFinderSecondaryTab.SAVED;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [isLoadingSavedCounts, setIsLoadingSavedCounts] = useState(false);
  const [apiParam, setApiParam] = useState<any>({
    page: 1,
  });
  const [savedApiParam, setSavedApiParam] = useState<any>({
    start: 1,
    take: 25,
  });
  const [companiesData, setCompaniesData] = useState<{
    profiles: V2Company[];
    pagination: {
      page: number;
      limit: number;
      totalRows: number;
    } | null;
  }>({
    profiles: [],
    pagination: null,
  });
  const [countsData, setCountsData] = useState<
    GetCompaniesV2CountResponse['payload'] | null
  >(null);
  const [countsError, setCountsError] = useState<boolean>(false);
  const [companyListsData, setCompanyListsData] = useState<any[]>([]);
  const [isLoadingLists, setIsLoadingLists] = useState(false);
  const [signalsListData, setSignalsListData] = useState<
    Array<{ key: string; value: string }>
  >([]);
  const [newsListData, setNewsListData] = useState<
    Array<{ key: string; value: string }>
  >([]);
  const previousSearchParamsRef = useRef<string>('');
  const previousSubTabRef = useRef<LeadFinderSecondaryTab | null>(null);
  const previousActiveLeadTypeRef = useRef<LeadFinderPrimaryTab | undefined>(
    undefined,
  );
  const lastAppliedNonSavedFiltersRef = useRef<Record<string, any> | null>(
    null,
  );
  const currentRequestIdRef = useRef(0);
  const currentRequestKeyRef = useRef<string>('');
  const activeSubTabRef = useRef(activeSubTab);
  const showingFilteredCountsRef = useRef(false);

  const leadFinderBulkOperationLimitValue = leadFinderBulkOperationLimit();

  // For V2 location filter: split by comma and take first part for API payload
  const splitLocationValue = (value: string): string => {
    if (value.includes(',')) {
      return value.split(',')[0].trim();
    }
    return value;
  };

  // Format date with user timezone for API requests
  const formatDateWithTimezone = useCallback(
    (date: Date, isEndOfDay = false): string => {
      const userTimezone = getUserTimeZone() || 'UTC';
      let dt = DateTime.fromJSDate(date).setZone(userTimezone);

      if (isEndOfDay) {
        dt = dt.endOf('day');
      } else {
        dt = dt.startOf('day');
      }

      return dt.toISO({ includeOffset: true }) || '';
    },
    [],
  );

  const handleGeneratePayload = useCallback(
    (source?: string | URLSearchParams) => {
      const queryParamsUpdated =
        source === undefined
          ? new URLSearchParams(window.location?.search)
          : new URLSearchParams(source ?? '');
      const payload: any = {};

      // Helper to get includes/excludes structure
      const getIncludesExcludes = (
        includeKey: string,
        excludeKey?: string,
        options: {
          exactMatch?: boolean;
          isUnknown?: number;
        } = {},
      ) => {
        if (options.isUnknown === 1) {
          return { isUnknown: 1 };
        }

        const includes = queryParamsUpdated.getAll(includeKey);
        const excludes = excludeKey
          ? queryParamsUpdated.getAll(excludeKey)
          : [];

        if (
          options.isUnknown === 0 &&
          includes.length === 0 &&
          excludes.length === 0
        ) {
          return { isUnknown: 0 };
        }

        if (
          includes.length === 0 &&
          excludes.length === 0 &&
          options.isUnknown === undefined
        ) {
          return null;
        }

        const result: any = {};

        if (includes.length > 0) {
          result.includes = includes.map((v) => v.replace(/"/g, ''));
        } else {
          result.includes = [];
        }

        if (excludes.length > 0) {
          result.excludes = excludes.map((v) => v.replace(/"/g, ''));
        } else if (excludeKey) {
          result.excludes = [];
        }

        if (options.exactMatch !== undefined) {
          result.exactMatch = options.exactMatch ? 1 : 0;
        }

        if (options.isUnknown !== undefined) {
          result.isUnknown = options.isUnknown;
        }

        return result;
      };

      // Helper to get min/max range structure
      const getMinMaxRange = (key: string, hasIsUnknownFlag = false) => {
        const value = queryParamsUpdated.get(key);
        const unknownValue = hasIsUnknownFlag
          ? queryParamsUpdated.get(`${key}_isUnknown`)
          : undefined;

        if (unknownValue === '1') {
          return { isUnknown: 1 };
        }

        if (unknownValue === '0' && !value) {
          return { isUnknown: 0 };
        }

        if (!value && unknownValue === undefined) {
          return null;
        }

        if (value) {
          // Handle less than format
          if (value.startsWith('<')) {
            const maxValue = value.replace('<', '').trim();
            const result: any = { min: '', max: maxValue };
            if (unknownValue !== undefined) {
              result.isUnknown = unknownValue === '1' ? 1 : 0;
            }
            return result;
          }

          // Handle greater than format
          if (value.endsWith('+')) {
            const minValue = value.replace('+', '').trim();
            const result: any = { min: minValue, max: '' };
            if (unknownValue !== undefined) {
              result.isUnknown = unknownValue === '1' ? 1 : 0;
            }
            return result;
          }

          // For company_founded_year, convert relative keys to actual year range
          let processedValue = value;
          if (key === 'company_founded_year') {
            const convertedValue = convertYearKeyToRange(value);
            if (convertedValue) {
              processedValue = convertedValue;
            }
          }

          // Parse dropdown value (e.g., "1-5" or "10-100")
          const [min, max] = processedValue.split('-');
          const result: any = { min, max };

          if (unknownValue !== undefined) {
            result.isUnknown = unknownValue === '1' ? 1 : 0;
          }

          return result;
        }

        return null;
      };

      const getSingleValue = (
        key: string,
        options: { exactMatch?: boolean } = {},
      ) => {
        const value = (queryParamsUpdated.get(key) || '')
          .replace(/"/g, '')
          .trim();
        if (!value) return null;
        if (options.exactMatch !== undefined) {
          return { value, exactMatch: options.exactMatch ? 1 : 0 };
        }
        return value;
      };

      const getDateRange = (key: string) => {
        const value = (queryParamsUpdated.get(key) || '').trim();
        if (!value) return null;
        if (value === 'all time') return { allTime: 1 };
        if (value === 'last_30_days') {
          const end = new Date();
          const start = new Date();
          start.setDate(start.getDate() - 30);
          return {
            min: start.toISOString().split('T')[0],
            max: end.toISOString().split('T')[0],
          };
        }
        if (value === 'last_90_days') {
          const end = new Date();
          const start = new Date();
          start.setDate(start.getDate() - 90);
          return {
            min: start.toISOString().split('T')[0],
            max: end.toISOString().split('T')[0],
          };
        }
        if (value === 'last_6_months') {
          const end = new Date();
          const start = new Date();
          start.setMonth(start.getMonth() - 6);
          return {
            min: start.toISOString().split('T')[0],
            max: end.toISOString().split('T')[0],
          };
        }
        if (value === 'last_year') {
          const end = new Date();
          const start = new Date();
          start.setFullYear(start.getFullYear() - 1);
          return {
            min: start.toISOString().split('T')[0],
            max: end.toISOString().split('T')[0],
          };
        }
        if (value.includes(' - ')) {
          const [min, max] = value.split(' - ').map((v) => v.trim());
          return { min, max };
        }
        return { min: value, max: value };
      };

      // Company IDs and Company Name
      // Dropdown selections have numeric IDs -> company_ids
      // Custom text entries (non-numeric) -> company_name
      const companyIdsData = getIncludesExcludes(
        'company_ids',
        'excludeCompany_ids',
      );

      const customNameIncludes: string[] = [];
      const customNameExcludes: string[] = [];

      if (companyIdsData) {
        if (companyIdsData.includes) {
          const numericIncludes: number[] = [];
          companyIdsData.includes.forEach((val: string) => {
            if (isPositiveCompanyId(val)) {
              numericIncludes.push(Number(val));
            } else {
              customNameIncludes.push(val);
            }
          });
          companyIdsData.includes = numericIncludes;
        }
        if (companyIdsData.excludes) {
          const numericExcludes: number[] = [];
          companyIdsData.excludes.forEach((val: string) => {
            if (isPositiveCompanyId(val)) {
              numericExcludes.push(Number(val));
            } else {
              customNameExcludes.push(val);
            }
          });
          companyIdsData.excludes = numericExcludes;
        }
        if (
          companyIdsData.includes?.length > 0 ||
          companyIdsData.excludes?.length > 0
        ) {
          payload.company_ids = companyIdsData;
        }
      }

      // Company Name (from bulk search + custom text entries) with includes/excludes/exactMatch
      const companyNameData = getIncludesExcludes(
        'company_name',
        'excludeCompany_name',
        {
          exactMatch: queryParamsUpdated.get('company_name_exactMatch') === '1',
        },
      );

      // Merge custom text entries from company_ids into company_name
      if (customNameIncludes.length > 0 || customNameExcludes.length > 0) {
        const nameIncludes = [
          ...(companyNameData?.includes || []),
          ...customNameIncludes,
        ];
        const nameExcludes = [
          ...(companyNameData?.excludes || []),
          ...customNameExcludes,
        ];
        payload.company_name = {
          ...(companyNameData || {}),
          ...(nameIncludes.length > 0 ? { includes: nameIncludes } : {}),
          ...(nameExcludes.length > 0 ? { excludes: nameExcludes } : {}),
        };
      } else if (companyNameData) {
        payload.company_name = companyNameData;
      }

      // Company Domain with includes/excludes
      const companyDomainData = getIncludesExcludes(
        'company_domain',
        'excludeCompany_domain',
      );
      if (companyDomainData) {
        payload.company_domain = companyDomainData;
      }

      // HQ Location
      const hqLocationExactMatch =
        queryParamsUpdated.get('company_hq_location_exactMatch') === '1' ||
        queryParamsUpdated.get('company_hq_location_v2_exactMatch') === '1';
      const hqLocationData = getIncludesExcludes(
        'company_hq_location',
        'excludeCompany_hq_location',
        {
          exactMatch: hqLocationExactMatch,
        },
      );
      if (hqLocationData) {
        const processedIncludes =
          hqLocationData.includes?.length > 0
            ? getFilterValuesWithExtraValues(
              hqLocationData.includes,
              LOCATION_DATA,
              true,
            )
            : [];
        const processedExcludes =
          hqLocationData.excludes.length > 0
            ? getFilterValuesWithExtraValues(
              hqLocationData.excludes,
              LOCATION_DATA,
              true,
            )
            : [];
        payload.company_hq_location = {
          includes: processedIncludes.map(splitLocationValue),
          excludes: processedExcludes.map(splitLocationValue),
          exactMatch: hqLocationData.exactMatch,
        };
      }

      // HQ Location v2 — structured picks (Region/Country/State/City)
      const companyHqV2IncludesRaw = queryParamsUpdated.getAll(
        'company_hq_location_v2',
      );
      const companyHqV2ExcludesRaw = queryParamsUpdated.getAll(
        'excludeCompany_hq_location_v2',
      );
      const companyHqV2Includes = companyHqV2IncludesRaw
        .map((raw) => decodeLocationV2Entry(raw))
        .filter((e): e is NonNullable<typeof e> => e !== null)
        .map((e) => suggestionToLocationItem(e));
      const companyHqV2Excludes = companyHqV2ExcludesRaw
        .map((raw) => decodeLocationV2Entry(raw))
        .filter((e): e is NonNullable<typeof e> => e !== null)
        .map((e) => suggestionToLocationItem(e));
      if (companyHqV2Includes.length > 0 || companyHqV2Excludes.length > 0) {
        payload.company_hq_location_v2 = {
          includes: companyHqV2Includes,
          excludes: companyHqV2Excludes,
          exactMatch: hqLocationExactMatch ? 1 : 0,
        };
      }

      // Founded Year
      const foundedYearData = getMinMaxRange('company_founded_year');
      if (foundedYearData) payload.company_founded_year = foundedYearData;

      // Industry
      const industryData = getIncludesExcludes(
        'company_industry',
        'excludeCompany_industry',
      );
      if (industryData) {
        const processedIncludes =
          industryData.includes?.length > 0
            ? expandIndustryParentToChildren(
              industryData.includes,
              INDUSTRY_DATA,
            )
            : [];
        const processedExcludes =
          industryData.excludes?.length > 0
            ? expandIndustryParentToChildren(
              industryData.excludes,
              INDUSTRY_DATA,
            )
            : [];
        payload.company_industry = {
          includes: processedIncludes,
          excludes: processedExcludes,
        };
      }

      // Type
      const typeData = getIncludesExcludes('type', undefined, {
        exactMatch: queryParamsUpdated.get('type_exactMatch') === '1',
      });
      if (typeData) payload.type = typeData;

      // SIC Codes
      const sicCodesValue = getSingleValue('sic_codes');
      if (sicCodesValue) {
        payload.sic_codes = {
          includes: [sicCodesValue],
          exactMatch:
            queryParamsUpdated.get('sic_codes_exactMatch') === '1' ? 1 : 0,
        };
      }

      // NAICS Codes
      const naicsCodesValue = getSingleValue('naics_codes');
      if (naicsCodesValue) {
        payload.naics_codes = {
          includes: [naicsCodesValue],
          exactMatch:
            queryParamsUpdated.get('naics_codes_exactMatch') === '1' ? 1 : 0,
        };
      }

      // Is B2B (radio)
      if (queryParamsUpdated.has('is_b2b')) {
        const value = queryParamsUpdated.get('is_b2b');
        payload.is_b2b = value === '1' ? 1 : 0;
      }

      // Technologies Used
      const technologiesData = getIncludesExcludes(
        'technologies_used',
        undefined,
        // {
        //   exactMatch:
        //     queryParamsUpdated.get('technologies_used_exactMatch') === '1',
        // },
      );
      if (technologiesData) payload.technologies_used = technologiesData;

      // Competitors Company Name (for look alike)
      const competitorNames = queryParamsUpdated.getAll(
        'competitors_company_name',
      );
      if (competitorNames.length > 0) {
        const competitorIds = queryParamsUpdated.getAll(
          'competitors_company_id',
        );
        payload.look_alike_company_name = competitorNames.map((name, index) => {
          const cleanName = name.replace(/"/g, '');
          const item: { value: string; company_id?: number } = {
            value: cleanName,
          };
          const id = competitorIds[index];
          if (id && id !== '0' && id !== '') {
            item.company_id = Number(id);
          }
          return item;
        });
      }

      // Website Domain (for look alike)
      const websiteDomainData = getIncludesExcludes(
        'website_domain',
        undefined,
        {
          exactMatch:
            queryParamsUpdated.get('website_domain_exactMatch') === '1',
        },
      );
      if (websiteDomainData) {
        payload.look_alike_website_domain = websiteDomainData;
      }

      // Ownership Status (radio - Private/Public/Other)
      if (queryParamsUpdated.has('ownership_status')) {
        const value = queryParamsUpdated.get('ownership_status');
        payload.ownership_status = value;
      }

      // Company Bootstrapped (radio)
      if (queryParamsUpdated.has('company_bootstrapped')) {
        const value = queryParamsUpdated.get('company_bootstrapped');
        payload.company_bootstrapped = value === '1';
      }

      // LinkedIn URL
      const linkedinUrlData = getIncludesExcludes('linkedin_url', undefined, {
        exactMatch: queryParamsUpdated.get('linkedin_url_exactMatch') === '1',
      });
      if (linkedinUrlData) {
        payload.linkedin_url = linkedinUrlData;
      }

      // Followers Count LinkedIn
      const followersCountData = getMinMaxRange('followers_count_linkedin');
      if (followersCountData) {
        payload.followers_count_linkedin = followersCountData;
      }

      // Website
      const websiteData = getIncludesExcludes('website', undefined, {
        exactMatch: queryParamsUpdated.get('website_exactMatch') === '1',
      });
      if (websiteData) {
        payload.website = websiteData;
      }

      // Social URLs
      const socialUrlsData = getIncludesExcludes(
        'social_urls',
        undefined,
        //   {
        //   exactMatch: queryParamsUpdated.get('social_urls_exactMatch') === '1',
        // }
      );
      if (socialUrlsData) {
        payload.social_urls = socialUrlsData;
      }

      // Active Job Postings Title
      const activeJobPostingsTitleData = getIncludesExcludes(
        'active_job_postings_title',
        undefined,
        {
          exactMatch:
            queryParamsUpdated.get('active_job_postings_title_exactMatch') ===
            '1',
        },
      );
      if (activeJobPostingsTitleData) {
        payload.active_job_postings_title = activeJobPostingsTitleData;
      }

      // Active Job Postings Count (only when at least one job title is selected)
      const activeJobPostingsCountData = getMinMaxRange(
        'active_job_postings_count',
      );
      if (activeJobPostingsCountData) {
        payload.active_job_postings_count = activeJobPostingsCountData;
      }

      // Employee Count
      const employeesCountData = getMinMaxRange('company_size');
      if (employeesCountData) {
        payload.company_size = employeesCountData;
      }

      // Seniority ranges - new group dropdown format
      const employeeCountSeniority: Array<{ group: string; count: any }> = [];
      SENIORITY_GROUP_OPTIONS.forEach(({ paramKey }) => {
        const data = getMinMaxRange(paramKey);
        if (data) {
          employeeCountSeniority.push({ group: paramKey, count: data });
        }
      });
      if (employeeCountSeniority.length > 0) {
        payload.employee_count_seniority = employeeCountSeniority;
      }

      // Department ranges - new group dropdown format
      const employeeCountDepartment: Array<{ group: string; count: any }> = [];
      DEPARTMENT_GROUP_OPTIONS.forEach(({ paramKey }) => {
        const data = getMinMaxRange(paramKey);
        if (data) {
          employeeCountDepartment.push({ group: paramKey, count: data });
        }
      });
      if (employeeCountDepartment.length > 0) {
        payload.employee_count_department = employeeCountDepartment;
      }

      // Revenue - company_annual_revenue: { min, max }
      const annualRevenueData = getMinMaxRange('company_annual_revenue');
      if (annualRevenueData) {
        payload.company_annual_revenue = annualRevenueData;
      }

      // Funding - funding_rounds_name (no change)
      const fundingRoundsNameData = getIncludesExcludes(
        'funding_rounds_name',
        undefined,
        {
          exactMatch:
            queryParamsUpdated.get('funding_rounds_name_exactMatch') === '1',
        },
      );
      if (fundingRoundsNameData) {
        payload.funding_rounds_name = fundingRoundsNameData;
      }

      // Funding - company_funding_date: { startDate, endDate }
      const fundingRoundsDateData = getDateRange(
        'funding_rounds_announced_date',
      );
      if (fundingRoundsDateData) {
        payload.company_funding_date = fundingRoundsDateData;
      }

      // Funding - company_funding_amount: { min, max }
      const fundingRoundsAmountData = getMinMaxRange('company_funding_amount');
      if (fundingRoundsAmountData) {
        payload.company_funding_amount = fundingRoundsAmountData;
      }

      // Last Funding Round - last_funding_round_name: { includes }
      const lastFundingRoundNameData = getIncludesExcludes(
        'last_funding_round_name',
        undefined,
        {
          exactMatch:
            queryParamsUpdated.get('last_funding_round_name_exactMatch') ===
            '1',
        },
      );
      if (lastFundingRoundNameData) {
        payload.last_funding_round_name = lastFundingRoundNameData;
      }

      // Last Funding Round - last_funding_date: { startDate, endDate }
      const lastFundingRoundDateData = getDateRange(
        'last_funding_round_announced_date',
      );
      if (lastFundingRoundDateData) {
        payload.last_funding_round_announced_date = lastFundingRoundDateData;
      }

      // Last Funding Round - last_funding_amount: { min, max }
      const lastFundingRoundAmountData = getMinMaxRange(
        'last_funding_round_amount_raised',
      );
      if (lastFundingRoundAmountData) {
        payload.last_funding_round_amount_raised = lastFundingRoundAmountData;
      }

      // Salary by Job Title - base_salary: { title: { includes }, range: { min, max } }
      const baseSalaryTitleEntries: Array<{
        title: { includes: string[] };
        range: { min?: string; max?: string };
      }> = [];
      const jobTitleParamIndices = new Set<number>();
      queryParamsUpdated.forEach((_, paramKey) => {
        const match = paramKey.match(/^base_salary_title_(\d+)$/);
        if (match) {
          jobTitleParamIndices.add(parseInt(match[1], 10));
        }
      });
      jobTitleParamIndices.forEach((index) => {
        const jobTitleValue = queryParamsUpdated.get(
          `base_salary_title_${index}`,
        );
        const rangeData = getMinMaxRange(
          `base_salary_title_percentage_${index}`,
        );
        if (
          jobTitleValue &&
          jobTitleValue !== 'select' &&
          jobTitleValue !== '' &&
          rangeData
        ) {
          baseSalaryTitleEntries.push({
            title: { includes: [jobTitleValue] },
            range: rangeData,
          });
        }
      });
      if (baseSalaryTitleEntries.length > 0) {
        payload.base_salary = baseSalaryTitleEntries;
      }

      // Website visitor count by month
      const totalWebsiteVisitsData = getMinMaxRange(
        'total_website_visits_monthly',
      );
      if (totalWebsiteVisitsData) {
        payload.total_website_visits_monthly = totalWebsiteVisitsData;
      }

      const rankGlobalData = getMinMaxRange('rank_global');
      if (rankGlobalData) {
        payload.rank_global = rankGlobalData;
      }

      // Visitor by Country - visits_breakdown_by_country: [{ country, percentage }] (index-based params)
      const visitsBreakdownByCountryEntries: Array<{
        country: string;
        percentage: { min?: string; max?: string };
      }> = [];
      const countryParamIndices = new Set<number>();
      queryParamsUpdated.forEach((_, paramKey) => {
        const match = paramKey.match(/^visits_breakdown_by_country_(\d+)$/);
        if (match) {
          countryParamIndices.add(parseInt(match[1], 10));
        }
      });
      countryParamIndices.forEach((index) => {
        const countryValue = queryParamsUpdated.get(
          `visits_breakdown_by_country_${index}`,
        );
        const percentageData = getMinMaxRange(
          `visits_breakdown_by_country_percentage_${index}`,
        );
        if (
          countryValue &&
          countryValue !== 'select' &&
          countryValue !== '' &&
          percentageData
        ) {
          visitsBreakdownByCountryEntries.push({
            country: countryValue,
            percentage: percentageData,
          });
        }
      });
      if (visitsBreakdownByCountryEntries.length > 0) {
        payload.visits_breakdown_by_country = visitsBreakdownByCountryEntries;
      }

      // Visitor by Gender - visits_breakdown_by_gender: [{ gender, percentage }]
      const visitsBreakdownByGenderType = queryParamsUpdated.get(
        'visits_breakdown_by_gender_type',
      );
      const visitsBreakdownByGenderRangeData = getMinMaxRange(
        'visits_breakdown_by_gender_range',
      );
      if (
        (visitsBreakdownByGenderType === VisitorByGenderFilter.MALE ||
          visitsBreakdownByGenderType === VisitorByGenderFilter.FEMALE) &&
        visitsBreakdownByGenderRangeData
      ) {
        payload.visits_breakdown_by_gender = [
          {
            gender: visitsBreakdownByGenderType,
            percentage: visitsBreakdownByGenderRangeData,
          },
        ];
      }

      // Employee Review count
      const employeeReviewsTotalCountData = getMinMaxRange(
        'employee_reviews_total_count',
      );
      if (employeeReviewsTotalCountData) {
        payload.employee_reviews_total_count = employeeReviewsTotalCountData;
      }

      const employeeReviewsAggregateScoreData = getMinMaxRange(
        'employee_reviews_aggregate_score',
      );
      if (employeeReviewsAggregateScoreData) {
        payload.employee_reviews_aggregate_score = employeeReviewsAggregateScoreData;
      }

      const employeeReviewsBusinessOutlookData = getMinMaxRange(
        'employee_reviews_business_outlook',
      );
      if (employeeReviewsBusinessOutlookData) {
        payload.employee_reviews_business_outlook = employeeReviewsBusinessOutlookData;
      }

      const employeeReviewsCeoApprovalData = getMinMaxRange(
        'employee_reviews_ceo_approval',
      );
      if (employeeReviewsCeoApprovalData) {
        payload.employee_reviews_ceo_approval = employeeReviewsCeoApprovalData;
      }

      const employeeReviewsCareerOpportunitiesData = getMinMaxRange(
        'employee_reviews_career_opportunities',
      );
      if (employeeReviewsCareerOpportunitiesData) {
        payload.employee_reviews_career_opportunities = employeeReviewsCareerOpportunitiesData;
      }

      const employeeReviewsRecommendData = getMinMaxRange(
        'employee_reviews_recommend',
      );
      if (employeeReviewsRecommendData) {
        payload.employee_reviews_recommend = employeeReviewsRecommendData;
      }

      const employeeReviewsWorkLifeBalanceData = getMinMaxRange(
        'employee_reviews_work_life_balance',
      );
      if (employeeReviewsWorkLifeBalanceData) {
        payload.employee_reviews_work_life_balance = employeeReviewsWorkLifeBalanceData;
      }

      const employeeReviewsCultureValuesData = getMinMaxRange(
        'employee_reviews_culture_values',
      );
      if (employeeReviewsCultureValuesData) {
        payload.employee_reviews_culture_values = employeeReviewsCultureValuesData;
      }

      // Estimated Product Review Score
      const productReviewsAggregateScoreData = getMinMaxRange(
        'product_reviews_aggregate_score',
      );
      if (productReviewsAggregateScoreData) {
        payload.product_reviews_aggregate_score = productReviewsAggregateScoreData;
      }

      const productReviewsCountData = getMinMaxRange('product_reviews_count');
      if (productReviewsCountData) {
        payload.product_reviews_count = productReviewsCountData;
      }

      // Product Review Score Change
      const productReviewsScoreChangedurationData = getIncludesExcludes(
        'product_reviews_score_change_duration',
      );
      const productReviewsScoreChangeDirectionData = getIncludesExcludes(
        'product_reviews_score_change_direction',
      );
      const productReviewsScoreChangePointsRangeData = getMinMaxRange(
        'product_reviews_score_change_points_range',
      );
      if (
        productReviewsScoreChangedurationData?.includes?.length > 0 &&
        productReviewsScoreChangeDirectionData?.includes?.length > 0 &&
        productReviewsScoreChangePointsRangeData
      ) {
        payload.product_reviews_score_change = {
          duration: productReviewsScoreChangedurationData?.includes[0],
          direction: productReviewsScoreChangeDirectionData?.includes[0],
          points_range: productReviewsScoreChangePointsRangeData,
        };
      }

      // Signals & News (no exclude, convert to numeric IDs)
      const signalsData = getIncludesExcludes('signals');
      if (
        signalsData &&
        signalsData.includes &&
        signalsData.includes.length > 0
      ) {
        const numericIds = signalsData.includes
          .map((id) => Number(id))
          .filter((n) => !Number.isNaN(n));
        if (numericIds.length > 0) {
          payload.signalIds = numericIds;
        }
      }

      const newsData = getIncludesExcludes('news');
      if (newsData && newsData.includes && newsData.includes.length > 0) {
        const numericIds = newsData.includes
          .map((id) => Number(id))
          .filter((n) => !Number.isNaN(n));
        if (numericIds.length > 0) {
          payload.newsIds = numericIds;
        }
      }

      // Keyword
      const categoriesAndKeywordsData = getIncludesExcludes(
        'keywords',
        undefined,
        // {
        //   exactMatch:
        //     queryParamsUpdated.get('categories_and_keywords_exactMatch') === '1',
        // },
      );
      if (categoriesAndKeywordsData) {
        payload.keywords = categoriesAndKeywordsData;
      }

      // Company List (include/exclude)
      const companyListIncludes = queryParamsUpdated.getAll('company_list');
      const companyListExcludes = queryParamsUpdated.getAll(
        'excludeCompany_list',
      );
      const companyList: any = {};

      if (companyListIncludes.length > 0) {
        const includes = companyListIncludes
          .map((id) => parseInt(id.replace(/"/g, '').trim(), 10))
          .filter((id) => !Number.isNaN(id) && id > 0);
        if (includes.length > 0) {
          companyList.includes = includes;
        }
      }

      if (companyListExcludes.length > 0) {
        const excludes = companyListExcludes
          .map((id) => parseInt(id.replace(/"/g, '').trim(), 10))
          .filter((id) => !Number.isNaN(id) && id > 0);
        if (excludes.length > 0) {
          companyList.excludes = excludes;
        }
      }

      if (Object.keys(companyList).length > 0) {
        payload.companyList = companyList;
      }

      return payload;
    },
    [],
  );

  const handleGenerateAPIParam = useCallback(
    (subTab: LeadFinderSecondaryTab) => {
      const filtersPayload = handleGeneratePayload();
      const paginationPayload =
        subTab === LeadFinderSecondaryTab.SAVED ? savedApiParam : apiParam;
      const payload = {
        ...paginationPayload,
        ...filtersPayload,
      };
      return payload;
    },
    [apiParam, savedApiParam, handleGeneratePayload],
  );

  const handleRouteChange = useCallback(() => {
    // Clear companies data when route changes
    setCompaniesData({
      profiles: [],
      pagination: null,
    });
  }, []);

  const handleRecentSearch = useCallback(
    (source?: URLSearchParams) => {
      if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
        return;
      }
      try {
        const recentPayload: any = source
          ? handleGeneratePayload(source)
          : handleGeneratePayload();
        const meaningfulParams = Object.keys(recentPayload).filter(
          (key) => key !== 'start' && key !== 'take' && key !== 'page',
        );
        if (meaningfulParams.length === 0 || !userEmail) {
          return;
        }

        const storedUrlsKey = V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY;
        const storedUrls = JSON.parse(
          StorageUtil.getItem(storedUrlsKey) || '{}',
        );
        if (!storedUrls[userEmail]) {
          storedUrls[userEmail] = [];
        }

        // For bulk search (source provided), URL has no params since filters aren't in the URL
        const currentUrlWithParams = source
          ? location.pathname
          : location.pathname + location.search;
        const timestamp = new Date().toISOString();

        const existingUrlIndex = storedUrls[userEmail].findIndex(
          (urlData: any) =>
            urlData?.url?.toLowerCase() === currentUrlWithParams?.toLowerCase(),
        );

        const maxUrls = 4;
        const filterParams = source || new URLSearchParams(location.search);
        const displayPayload = buildRecentSearchDisplayPayload(
          recentPayload,
          filterParams,
          true,
        );
        const updatedEntry = {
          url: currentUrlWithParams,
          timestamp,
          payload: recentPayload,
          displayPayload,
          ...(source ? { pendingFiltersStr: source.toString() } : {}),
        };

        if (existingUrlIndex !== -1) {
          storedUrls[userEmail].unshift(
            storedUrls[userEmail].splice(existingUrlIndex, 1)[0],
          );
          storedUrls[userEmail][0] = updatedEntry;
        } else {
          storedUrls[userEmail].unshift(updatedEntry);
        }

        storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
        StorageUtil.setItem(storedUrlsKey, JSON.stringify(storedUrls));
      } catch (_e) {
        // no-op
      }
    },
    [
      activeSubTab,
      handleGeneratePayload,
      location.pathname,
      location.search,
      userEmail,
    ],
  );

  const handleFetchCounts = useCallback(async (payload: any) => {
    try {
      setIsLoadingCounts(true);
      setCountsError(false);
      // Reset until we know this query is filtered (set below for verified users).
      showingFilteredCountsRef.current = false;
      // Skip API call if email is not verified and show dummy counts
      if (!isEmailVerified) {
        setCountsData((prevState) => ({
          ...prevState,
          totalCount: DEFAULT_TOTAL_COMPANIES_COUNT,
          netNewCount: DEFAULT_TOTAL_COMPANIES_COUNT,
        }));
        return;
      }

      const { page, start, take, ...countsPayload } = payload;
      // Default (no-filter) view: cache the total and derive netNew from the
      // saved count instead of hitting the expensive { tab: 'total' } query.
      // purgeCache forces a real refresh.
      const hasSearchFilters = Object.keys(countsPayload)?.length > 0;
      showingFilteredCountsRef.current = hasSearchFilters;
      const purgeCache =
        new URLSearchParams(window.location?.search).get('purgeCache') === '1';
      if (!hasSearchFilters && !purgeCache) {
        setCountsData((prevState) => ({
          ...prevState,
          totalCount: DEFAULT_TOTAL_COMPANIES_COUNT,
          netNewCount: deriveDefaultNetNew(
            prevState?.savedCount,
            DEFAULT_TOTAL_COMPANIES_COUNT,
          ),
        }));
        return;
      }
      const res = await getCompaniesV2Count(countsPayload);
      if (res?.payload) {
        setCountsData((prevState) => ({
          ...prevState,
          totalCount: res.payload.totalCount,
          netNewCount: res.payload.netNewCount,
        }));
      }
    } catch (e: any) {
      setCountsError(true);
      setCountsData((prevState) => ({
        ...prevState,
        totalCount: 0,
        netNewCount: 0,
      }));
      if (!e?.isHandled) {
        toaster.error(
          e.message || "Couldn't fetch company counts. Please try again.",
        );
      }
    } finally {
      setIsLoadingCounts(false);
    }
  }, []);

  // Build saved companies payload from URL params
  const buildSavedCompaniesPayload = useCallback(
    (pagination: { start: number; take: number }) => {
      const queryParams = new URLSearchParams(window.location?.search);
      const payload: any = {
        start: pagination.start || 1,
        take: pagination.take || 25,
      };

      // Extract search
      const search = queryParams.get('search');
      if (search) {
        payload.search = search;
      }

      // Extract tags and convert to tagIds
      const tags = queryParams.get('tags');
      if (tags) {
        const tagIds = tags
          .split(',')
          .map((tag) => parseInt(tag.trim(), 10))
          .filter((id) => !Number.isNaN(id) && id > 0);
        if (tagIds.length > 0) {
          payload.tagIds = tagIds;
        }
      }

      // Extract createdDate and format with timezone
      const createdDate = queryParams.get('createdDate');
      if (createdDate) {
        const dateArr = createdDate.split('-');
        if (dateArr.length === 2) {
          const startDate = new Date(dateArr[0]);
          const endDate = new Date(dateArr[1]);
          endDate.setHours(23, 59, 59, 999);
          const formattedStart = formatDateWithTimezone(startDate, false);
          const formattedEnd = formatDateWithTimezone(endDate, true);
          if (formattedStart && formattedEnd) {
            payload.createdDate = `${formattedStart},${formattedEnd}`;
          }
        }
      }

      // Extract company_list for listIds (includes only). Exclude-listId is not
      // used by the saved-companies tab (G3): the backend ignores it, so we no
      // longer read excludeCompany_list or send listIds.excludes.
      const companyListIncludesStr = queryParams.get('company_list');
      const listIds: any = {};

      if (companyListIncludesStr) {
        const includes = companyListIncludesStr
          .split(',')
          .map((id) => parseInt(id.replace(/"/g, '').trim(), 10))
          .filter((id) => !Number.isNaN(id) && id > 0);
        if (includes.length > 0) {
          listIds.includes = includes;
        }
      }

      if (Object.keys(listIds).length > 0) {
        payload.listIds = listIds;
      }

      const jobIdParam = queryParams.get('jobId');
      if (jobIdParam) {
        const parsedJobId = Number(jobIdParam);
        payload.jobId = !Number.isNaN(parsedJobId) ? parsedJobId : jobIdParam;
      }

      return payload;
    },
    [formatDateWithTimezone],
  );

  const handleFetchSavedCounts = useCallback(async (payload: any) => {
    // In the cached default view the saved count is the only value fetched, so
    // it owns the authoritative netNew = cachedTotal - savedCount.
    const savedParams = new URLSearchParams(window.location?.search);
    const savedPurgeCache = savedParams.get('purgeCache') === '1';
    ['page', 'start', 'take', 'purgeCache', 'tab'].forEach((k) =>
      savedParams.delete(k),
    );
    const isCachedDefaultView =
      isEmailVerified &&
      savedParams.toString().length === 0 &&
      !savedPurgeCache &&
      !skipUrlPushForBulkSearchRef?.current;
    // On the Saved tab handleFetchCounts is always called without filters
    // ({ page: 1 }), so it never fetches a real netNew — the saved-counts
    // response owns the netNew derivation there. Without this, refreshing the
    // Saved tab with an active filter leaves the "New" count stuck on its
    // loading skeleton: isCachedDefaultView is false (URL has filters) so
    // nothing ever sets netNewCount (BE-2529). Read the live subtab from the
    // ref since this callback is memoized with empty deps.
    // BUT when Total/New currently hold filtered counts (e.g. switching to
    // Saved after filtering), keep those filtered counts instead of resetting
    // netNew to the cached default (BE-2531).
    const ownsNetNewDerivation =
      (isCachedDefaultView ||
        activeSubTabRef.current === LeadFinderSecondaryTab.SAVED) &&
      !showingFilteredCountsRef.current;
    try {
      setIsLoadingSavedCounts(true);
      if (!isEmailVerified) {
        // Skip API call if email is not verified and show 0 for saved count
        setCountsData((prevState) => ({
          ...prevState,
          savedCount: 0,
        }));
        return;
      }

      const { start, take, ...countsPayload } = payload;
      const res = await getSavedCompaniesV2Count(countsPayload);
      if (res?.payload) {
        const resolvedSavedCount =
          (res.payload as any).savedCount ??
          (res.payload as any).revealedCount ??
          0;
        setCountsData((prevState) => ({
          ...prevState,
          savedCount: resolvedSavedCount,
          ...(ownsNetNewDerivation
            ? {
              netNewCount: deriveDefaultNetNew(
                resolvedSavedCount,
                DEFAULT_TOTAL_COMPANIES_COUNT,
              ),
            }
            : {}),
        }));
      }
    } catch (e: any) {
      setCountsData((prevState) => ({
        ...prevState,
        // Preserve the last-known savedCount: the saved-tab total is now sourced
        // solely from this value, so resetting to 0 would flash an empty total
        // over a populated list on a transient counts-API failure.
        savedCount: prevState?.savedCount ?? 0,
        ...(ownsNetNewDerivation
          ? { netNewCount: DEFAULT_TOTAL_COMPANIES_COUNT }
          : {}),
      }));
      if (!e?.isHandled) {
        toaster.error(
          e.message || "Couldn't fetch saved company counts. Please try again.",
        );
      }
    } finally {
      setIsLoadingSavedCounts(false);
    }
  }, []);

  const handleFetchCompanyLists = useCallback(
    async (tab: LeadFinderListTab) => {
      try {
        setIsLoadingLists(true);
        const res = await getCompanyListsAPI(tab);
        if (res?.payload && Array.isArray(res.payload)) {
          // Transform API response to match SavedListData type
          const transformedLists = res.payload.map((list: any) => ({
            id: list.id,
            label: list.name,
            visibility: list.visibility as LeadFinderListVisibility,
            count: list.count,
            owner: {
              firstName: list.user?.firstName || '',
              lastName: list.user?.lastName || '',
            },
            isDefault: list.isDefault,
          }));
          setCompanyListsData(transformedLists);
        }
      } catch (e: any) {
        setCompanyListsData([]);
        if (!e?.isHandled) {
          toaster.error(
            e.message || "Couldn't fetch company lists. Please try again.",
          );
        }
      } finally {
        setIsLoadingLists(false);
      }
    },
    [],
  );

  const handleFetchCompanies = useCallback(
    async (data: any, subTab: LeadFinderSecondaryTab) => {
      // eslint-disable-next-line no-plusplus
      const requestId = ++currentRequestIdRef.current;
      const requestKey = `${subTab}::${window.location?.search || ''}`;
      currentRequestKeyRef.current = requestKey;
      const isSaved = subTab === LeadFinderSecondaryTab.SAVED;
      try {
        setIsLoading(true);
        let res: any = {};

        if (!isSaved) {
          if (data && typeof data === 'object') {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { page, start, take, ...rest } = data;
            if (Object.keys(rest || {}).length > 0) {
              lastAppliedNonSavedFiltersRef.current = rest;
            }
          }
          const params = new URLSearchParams();
          params.set('tab', activeSubTab);
          res = await getCompaniesV2(params.toString(), data);
        } else {
          res = await getSavedCompanies(data);
        }

        if (res?.payload) {
          if (
            subTab !== activeSubTabRef.current ||
            requestId !== currentRequestIdRef.current ||
            requestKey !== currentRequestKeyRef.current
          ) {
            return;
          }
          if (isSaved) {
            // Listing response no longer carries an authoritative total; the
            // saved-companies total comes from the counts API (countsData.savedCount).
            // Seed from the already-known savedCount; the counts-sync effect
            // corrects it once /counts resolves.
            const savedPayload = res?.payload || {};
            const savedProfiles =
              savedPayload?.companies || savedPayload?.profiles || [];
            const savedPagination = savedPayload?.pagination || {};
            const start = savedPagination.start || data?.start || 1;
            const take = savedPagination.take || data?.take || 25;
            const page = Math.floor((start - 1) / take) + 1;

            setCompaniesData({
              profiles: [...savedProfiles],
              pagination: {
                page,
                limit: take,
                totalRows: countsData?.savedCount ?? 0,
              },
            });
          } else {
            const resolvedProfiles = Array.isArray(res?.payload)
              ? res?.payload
              : res?.payload?.profiles || [];

            // Use countsData for totalRows if available, otherwise use length of returned data
            let totalRows = resolvedProfiles.length;
            if (countsData) {
              if (activeSubTab === LeadFinderSecondaryTab.TOTAL) {
                totalRows = countsData.totalCount || 0;
              } else if (activeSubTab === LeadFinderSecondaryTab.NEW) {
                totalRows = countsData.netNewCount || 0;
              }
            }

            setCompaniesData({
              profiles: [...resolvedProfiles],
              pagination: {
                page: data?.page || 1,
                limit: LEADS_PER_PAGE,
                totalRows,
              },
            });
          }
        }
      } catch (e: any) {
        const emptyPagination = {
          page: 1,
          limit: isSaved ? 25 : LEADS_PER_PAGE,
          totalRows: 0,
        };

        if (!e?.isHandled) {
          toaster.error(
            e.message || "Couldn't fetch companies. Please try again.",
          );
        }

        if (
          requestId === currentRequestIdRef.current &&
          subTab === activeSubTabRef.current &&
          requestKey === currentRequestKeyRef.current
        ) {
          setCompaniesData({
            pagination: emptyPagination,
            profiles: [],
          });
        }
      } finally {
        if (
          requestId === currentRequestIdRef.current &&
          requestKey === currentRequestKeyRef.current
        ) {
          setIsLoading(false);
        }
      }
    },
    [activeSubTab],
  );

  // Fetch signals and news data — only when company tab is active
  useEffect(() => {
    if (activeLeadType === LeadFinderPrimaryTab.CSV_ENRICHMENT) return;

    const fetchSignalsAndNews = async () => {
      const results = await Promise.allSettled([getSignalsAPI(), getNewsAPI()]);

      // Handle signals
      if (results[0].status === 'fulfilled' && results[0].value?.payload) {
        const signalsData = Array.isArray(results[0].value.payload)
          ? results[0].value.payload
          : [];
        const transformedSignals = signalsData.map((item: any) => ({
          key: String(item.id),
          value: item.name,
          tooltipText: item.logic,
        }));
        setSignalsListData(transformedSignals);
      } else {
        setSignalsListData([]);
      }

      // Handle news
      if (results[1].status === 'fulfilled' && results[1].value?.payload) {
        const newsData = Array.isArray(results[1].value.payload)
          ? results[1].value.payload
          : [];
        const transformedNews = newsData.map((item: any) => ({
          key: String(item.id),
          value: item.name,
          tooltipText: item.description,
        }));
        setNewsListData(transformedNews);
      } else {
        setNewsListData([]);
      }
    };

    fetchSignalsAndNews();
  }, []);

  useEffect(() => {
    const switchedToCompanyTab =
      activeLeadType === LeadFinderPrimaryTab.COMPANY &&
      previousActiveLeadTypeRef.current !== LeadFinderPrimaryTab.COMPANY;

    if (shouldBlockAccess) {
      if (activeLeadType !== LeadFinderPrimaryTab.COMPANY) {
        previousActiveLeadTypeRef.current = activeLeadType;
      }
      previousSearchParamsRef.current = location.search;
      return;
    }

    const currentSearchParams = location.search;
    const previousSearchParams = previousSearchParamsRef.current;
    const previousSubTab = previousSubTabRef.current;

    // Detect bulk URL clear: skipUrlPushForBulkSearchRef is set and URL just went empty
    const isBulkUrlClear =
      !!skipUrlPushForBulkSearchRef?.current &&
      (() => {
        const params = new URLSearchParams(currentSearchParams);
        params.delete('page');
        params.delete('start');
        params.delete('take');
        return params.toString().length === 0;
      })() &&
      currentSearchParams !== previousSearchParams;
    const isBulkSavedToSearchSwitch =
      !!skipUrlPushForBulkSearchRef?.current &&
      previousSubTab === LeadFinderSecondaryTab.SAVED &&
      !!lastAppliedNonSavedFiltersRef.current &&
      Object.keys(lastAppliedNonSavedFiltersRef.current || {}).length > 0;

    // Cancel any in-flight request from the previous render (skip if bulk URL clear)
    if (!isBulkUrlClear) {
      currentRequestIdRef.current += 1;
      currentRequestKeyRef.current = `${activeSubTab}::${currentSearchParams || ''
        }`;
      setIsLoading(false);
    }

    // Only clear data when filters actually change (not during bulk URL clear)
    const isInitialLoad = previousSubTab === null;
    if (!isInitialLoad && !isBulkUrlClear && !isBulkSavedToSearchSwitch) {
      setCompaniesData({
        profiles: [],
        pagination: null,
      });
    }

    // Check if URL has filter params
    const hasFilterParams = () => {
      const params = new URLSearchParams(currentSearchParams);
      params.delete('page');
      params.delete('start');
      params.delete('take');
      return params.toString().length > 0;
    };

    const urlHasFilters = hasFilterParams();
    if (!skipUrlPushForBulkSearchRef?.current && !urlHasFilters) {
      lastAppliedNonSavedFiltersRef.current = null;
    }
    const hasPreservedBulkFilters =
      !!lastAppliedNonSavedFiltersRef.current &&
      Object.keys(lastAppliedNonSavedFiltersRef.current || {}).length > 0;
    const isBulkSearchMode =
      !!skipUrlPushForBulkSearchRef?.current ||
      (!urlHasFilters && hasPreservedBulkFilters);

    const fetchCompanies = async () => {
      if (isSavedSubTab) {
        const hasTotalCount = countsData?.totalCount !== undefined;
        const hasNetNewCount = countsData?.netNewCount !== undefined;
        if (!hasTotalCount || !hasNetNewCount) {
          handleFetchCounts({ page: 1 });
        }

        const resetPagination = {
          start: 1,
          take: 25,
        };
        setSavedApiParam({
          start: 0,
          take: 25,
        });

        const payload = buildSavedCompaniesPayload(resetPagination);

        handleFetchSavedCounts(payload);
        handleFetchCompanies(payload, activeSubTab);
        // Only fetch lists on initial load, not when switching to saved tab
        if (
          previousSubTab === null &&
          activeLeadType === LeadFinderPrimaryTab.COMPANY
        ) {
          handleFetchCompanyLists(LeadFinderListTab.Company);
        }
      } else {
        const resetPagination = {
          page: 1,
        };
        setApiParam(resetPagination);

        const filtersPayload =
          isBulkSearchMode && !urlHasFilters
            ? lastAppliedNonSavedFiltersRef.current || {}
            : handleGeneratePayload();
        const payload = {
          ...resetPagination,
          ...filtersPayload,
        };
        let isPartialProductReviewScoreChange = false;
        let isPartialBaseSalary = false;

        try {
          const params = new URLSearchParams(window.location?.search);

          // For the product review score change
          const keys = [
            'product_reviews_score_change_duration',
            'product_reviews_score_change_direction',
            'product_reviews_score_change_points_range',
          ];
          const presentCount = keys.reduce((acc, k) => {
            const v = params.get(k);
            return acc + (v !== null && String(v).trim() !== '' ? 1 : 0);
          }, 0);
          isPartialProductReviewScoreChange =
            presentCount > 0 && presentCount < keys.length;

          // For the base salary
          const indices = new Set<string>();
          params.forEach((_, key) => {
            const mTitle = key.match(/^base_salary_title_(\d+)$/);
            if (mTitle) indices.add(mTitle[1]);
            const mPercent = key.match(/^base_salary_title_percentage_(\d+)$/);
            if (mPercent) indices.add(mPercent[1]);
          });

          if (indices.size > 0) {
            let hasValidEntry = false;
            indices.forEach((idx) => {
              const jobTitleValue = params.get(`base_salary_title_${idx}`);
              const percentageValue = params.get(
                `base_salary_title_percentage_${idx}`,
              );
              const validJobTitle =
                jobTitleValue &&
                jobTitleValue !== 'select' &&
                jobTitleValue !== '';
              const validRange =
                percentageValue &&
                percentageValue !== 'select' &&
                String(percentageValue).trim() !== '';
              if (validJobTitle && validRange) {
                hasValidEntry = true;
              }
            });

            isPartialBaseSalary = !hasValidEntry;
          }
        } catch (_e) {
          // If an error occurs, both variables remain as false
        }

        if (!isPartialProductReviewScoreChange && !isPartialBaseSalary) {
          const skipCountsOnCompanyTabReturn =
            switchedToCompanyTab && !isInitialLoad && !urlHasFilters;

          const shouldTriggerCountsFromEffect =
            !isBulkSearchMode || urlHasFilters;

          if (!skipCountsOnCompanyTabReturn) {
            if (isBulkSearchMode) {
              const currentParams = new URLSearchParams(currentSearchParams);
              currentParams.delete('page');
              currentParams.delete('start');
              currentParams.delete('take');
              const isCurrentUrlEmpty = currentParams.toString().length === 0;

              const prevParams = new URLSearchParams(
                previousSearchParams || '',
              );
              prevParams.delete('page');
              prevParams.delete('start');
              prevParams.delete('take');
              const wasPrevUrlNonEmpty = prevParams.toString().length > 0;

              const { page, ...filtersOnly } = payload || {};
              const hasAnyFilters =
                Object.keys(filtersOnly || {}).length > 0 || urlHasFilters;

              if (
                hasAnyFilters &&
                !(isCurrentUrlEmpty && wasPrevUrlNonEmpty) &&
                Object.keys(filtersOnly || {}).length > 0 &&
                shouldTriggerCountsFromEffect
              ) {
                handleFetchCounts(payload);
              }
            } else if (shouldTriggerCountsFromEffect) {
              handleFetchCounts(payload);
            }
          }
        }

        // Re-fetch the account-wide saved count on initial load and whenever we
        // (re-)enter the cached default view (e.g. "Clear all") so netNew stays
        // fresh. Skipped when filters are applied or in bulk-search mode.
        const isDefaultNoFilterView =
          isEmailVerified && !urlHasFilters && !isBulkSearchMode;
        if (
          previousSubTab === null ||
          previousSubTab === LeadFinderSecondaryTab.SAVED ||
          isDefaultNoFilterView
        ) {
          handleFetchSavedCounts({});
        }
        if (
          previousSubTab === null ||
          previousSubTab === LeadFinderSecondaryTab.SAVED
        ) {
          if (activeLeadType === LeadFinderPrimaryTab.COMPANY) {
            handleFetchCompanyLists(LeadFinderListTab.Company);
          }
        }

        // Call search API if there are filters or if it's Total tab
        const { page, ...filters } = payload;
        const hasFilters =
          Object.keys(filters || {}).length > 0 || urlHasFilters;
        const isTotalToNewSwitch =
          (previousSubTab === LeadFinderSecondaryTab.TOTAL &&
            activeSubTab === LeadFinderSecondaryTab.NEW) ||
          (previousSubTab === LeadFinderSecondaryTab.NEW &&
            activeSubTab === LeadFinderSecondaryTab.TOTAL);
        const filtersChanged = currentSearchParams !== previousSearchParams;

        // Determine if we should fetch companies.
        const shouldFetch =
          // Fetch if there are filters and it's initial load
          (hasFilters && isInitialLoad) ||
          // Fetch if switching between Total and New tabs with filters
          (hasFilters && isTotalToNewSwitch) ||
          // Fetch if filters changed in URL
          (hasFilters && filtersChanged && urlHasFilters) ||
          // Fetch when switching back to company tab with filters (URL restored from state)
          (hasFilters && switchedToCompanyTab) ||
          isBulkSavedToSearchSwitch;

        if (shouldFetch) {
          handleFetchCompanies(payload, activeSubTab);
        }
      }
    };

    fetchCompanies();

    previousSearchParamsRef.current = currentSearchParams;
    previousSubTabRef.current = activeSubTab;
    previousActiveLeadTypeRef.current = activeLeadType;
  }, [
    activeSubTab,
    location.search,
    shouldBlockAccess,
    isSavedSubTab,
    activeLeadType,
    handleGeneratePayload,
    handleFetchCounts,
    handleFetchSavedCounts,
    handleFetchCompanies,
    buildSavedCompaniesPayload,
    handleFetchCompanyLists,
  ]);

  useEffect(() => {
    activeSubTabRef.current = activeSubTab;
  }, [activeSubTab]);

  // Update pagination when countsData or companiesData changes.
  // Saved tab: the listing response no longer carries an authoritative total,
  // so the saved-companies total is sourced from the counts API (savedCount).
  useEffect(() => {
    if (isSavedSubTab && countsData) {
      setCompaniesData((prevState) => {
        if (!prevState?.pagination) {
          return prevState;
        }
        const totalRows = countsData.savedCount ?? 0;
        if (prevState.pagination.totalRows !== totalRows) {
          return {
            ...prevState,
            pagination: { ...prevState.pagination, totalRows },
          };
        }
        return prevState;
      });
      return;
    }
    if (!isSavedSubTab && countsData) {
      setCompaniesData((prevState) => {
        // If no profiles, set totalRows to 0
        if (prevState?.profiles?.length === 0 && prevState?.pagination) {
          if (prevState.pagination.totalRows !== 0) {
            return {
              ...prevState,
              pagination: {
                ...prevState.pagination,
                totalRows: 0,
              },
            };
          }
          return prevState;
        }

        // Only update if we have profiles and pagination
        if (prevState?.profiles?.length > 0 && prevState?.pagination) {
          let totalRows = 0;
          if (activeSubTab === LeadFinderSecondaryTab.TOTAL) {
            totalRows = countsData.totalCount || 0;
          } else if (activeSubTab === LeadFinderSecondaryTab.NEW) {
            totalRows = countsData.netNewCount || 0;
          }

          // Only update if totalRows has changed
          if (prevState.pagination.totalRows !== totalRows) {
            return {
              ...prevState,
              pagination: {
                ...prevState.pagination,
                totalRows,
              },
            };
          }
        }
        return prevState;
      });
    }
  }, [
    countsData,
    activeSubTab,
    isSavedSubTab,
    companiesData?.profiles?.length,
    companiesData?.pagination?.totalRows,
  ]);

  const searchCompanies = useCallback(
    (payload?: any, performSearch = true) => {
      const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;

      if (!isSaved && performSearch) {
        try {
          const recentPayload: any = handleGeneratePayload();
          const meaningfulParams = Object.keys(recentPayload).filter(
            (key) => key !== 'start' && key !== 'take' && key !== 'page',
          );
          if (meaningfulParams.length > 0 && userEmail) {
            const storedUrlsKey = V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY;
            const storedUrls = JSON.parse(
              StorageUtil.getItem(storedUrlsKey) || '{}',
            );

            if (!storedUrls[userEmail]) {
              storedUrls[userEmail] = [];
            }

            const currentUrlWithParams =
              window.location.pathname + window.location.search;
            const timestamp = new Date().toISOString();

            const existingUrlIndex = storedUrls[userEmail].findIndex(
              (urlData: any) =>
                urlData?.url?.toLowerCase() ===
                currentUrlWithParams?.toLowerCase(),
            );

            const maxUrls = 4;
            const inlineDisplayPayload = buildRecentSearchDisplayPayload(
              recentPayload,
              new URLSearchParams(window.location.search),
              true,
            );
            const updatedEntry = {
              url: currentUrlWithParams,
              timestamp,
              payload: recentPayload,
              displayPayload: inlineDisplayPayload,
            };

            if (existingUrlIndex !== -1) {
              storedUrls[userEmail].unshift(
                storedUrls[userEmail].splice(existingUrlIndex, 1)[0],
              );
              storedUrls[userEmail][0] = updatedEntry;
            } else {
              storedUrls[userEmail].unshift(updatedEntry);
            }

            storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
            StorageUtil.setItem(storedUrlsKey, JSON.stringify(storedUrls));
          }
        } catch (_e) {
          // no-op
        }
      }

      if (payload) {
        const resetPagination = isSaved
          ? {
            start: 0,
            take: 25,
          }
          : {
            page: 1,
          };

        let finalPayload;
        if (isSaved) {
          // For saved tab, merge pagination with filters from URL
          // Convert 0-based start to 1-based for API
          const apiStart =
            (payload.start !== undefined ? payload.start : 0) + 1;
          finalPayload = buildSavedCompaniesPayload({
            start: apiStart,
            take: payload.take || 25,
          });
        } else {
          finalPayload = {
            ...resetPagination,
            ...payload,
          };
        }

        (isSaved ? setSavedApiParam : setApiParam)(resetPagination);
        if (performSearch) {
          handleFetchCompanies(finalPayload, activeSubTab);
        }
        return;
      }
      const updatedPayload = isSaved
        ? buildSavedCompaniesPayload({
          start: savedApiParam.start || 1,
          take: savedApiParam.take || 25,
        })
        : handleGenerateAPIParam(activeSubTab);
      if (performSearch) {
        handleFetchCompanies(updatedPayload, activeSubTab);
      }
    },
    [
      activeSubTab,
      handleGenerateAPIParam,
      handleFetchCompanies,
      buildSavedCompaniesPayload,
      savedApiParam,
      handleGeneratePayload,
      userEmail,
    ],
  );

  const handlePagination = useCallback(
    ({
      page,
      limit,
      filterSource,
    }: {
      page: number;
      limit?: number;
      filterSource?: string;
    }) => {
      if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
        const takeValue = limit || savedApiParam.take || 25;
        const startValue = (page - 1) * takeValue;
        const apiStartValue = startValue + 1;
        setSavedApiParam({
          start: apiStartValue,
          take: takeValue,
        });
        const updatedPayload = buildSavedCompaniesPayload({
          start: apiStartValue,
          take: takeValue,
        });
        handleFetchCompanies(updatedPayload, activeSubTab);
      } else {
        setApiParam({
          page,
        });
        const filtersPayload = handleGeneratePayload(filterSource);
        const updatedPayload = {
          page,
          ...filtersPayload,
        };
        handleFetchCompanies(updatedPayload, activeSubTab);
      }
    },
    [
      activeSubTab,
      savedApiParam,
      handleGeneratePayload,
      handleFetchCompanies,
      buildSavedCompaniesPayload,
    ],
  );

  const getCompanyNames = useCallback(
    async (
      filtersSource?: string | URLSearchParams,
    ): Promise<Array<{ companyId: number; companyName: string }>> => {
      try {
        let payload: any;

        if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
          // For Saved tab, use saved companies payload
          payload = buildSavedCompaniesPayload({
            start: (savedApiParam.start || 0) + 1,
            take: savedApiParam.take || 25,
          });

          payload.start = 1;
          payload.take =
            countsData?.savedCount > leadFinderBulkOperationLimitValue
              ? leadFinderBulkOperationLimitValue
              : countsData?.savedCount;

          if (payload.listIds) {
            payload.companyList = payload.listIds;
            delete payload.listIds;
          }
          if (payload.tagIds) {
            payload.tags = payload.tagIds;
            delete payload.tagIds;
          }
        } else {
          // For Total/Net New tabs, use search API payload
          const filtersPayload = handleGeneratePayload(filtersSource);
          const paginationPayload = apiParam;
          payload = {
            ...paginationPayload,
            ...filtersPayload,
          };
        }

        const response = await getCompanyNamesAPI(payload, activeSubTab);
        const companyNamesData =
          response?.payload || (Array.isArray(response) ? response : []);

        if (!Array.isArray(companyNamesData) || companyNamesData.length === 0) {
          return [];
        }

        return companyNamesData.map(
          (item: { companyId?: number; companyName?: string }) => ({
            companyId: item.companyId || 0,
            companyName: item.companyName || '',
          }),
        );
      } catch (error: any) {
        if (!error?.isHandled) {
          toaster.error(
            error?.message || 'Failed to get company names. Please try again.',
          );
        }
        return [];
      }
    },
    [
      activeSubTab,
      savedApiParam,
      apiParam,
      countsData,
      handleGeneratePayload,
      buildSavedCompaniesPayload,
    ],
  );

  const refreshSavedCounts = useCallback(
    () =>
      handleFetchSavedCounts(
        buildSavedCompaniesPayload({ start: 1, take: 25 }),
      ),
    [buildSavedCompaniesPayload, handleFetchSavedCounts],
  );

  return {
    isLoading,
    isLoadingCounts,
    isLoadingSavedCounts,
    companiesData,
    apiParam,
    savedApiParam,
    handlePagination,
    handleGeneratePayload,
    setCompaniesData,
    handleRouteChange,
    handleRecentSearch,
    searchCompanies,
    handleFetchCounts,
    handleFetchSavedCounts,
    countsData,
    countsError,
    companyListsData,
    isLoadingLists,
    refreshCompanyLists: () =>
      handleFetchCompanyLists(LeadFinderListTab.Company),
    refreshSavedCounts,
    getCompanyNames,
    signalsListData,
    newsListData,
  };
};
